"use client";

import { createContext, type ReactNode, useContext } from "react";

/**
 * Live content for the navigation's feature cards: the latest changelog
 * entry, the featured article and the featured case study. The loaders read
 * MDX from disk, so the root layout loads these on the server
 * (lib/navigation-features.ts) and only plain props reach the client nav.
 */
export type NavigationFeatures = {
  changelog: {
    slug: string;
    title: string;
    summary: string;
    dateLabel: string;
  } | null;
  blog: { slug: string; title: string; category: string; readTime: string };
  caseStudy: {
    slug: string;
    company: string;
    logo: { src: string; width: number; height: number };
    hero: { src: string; focus: string | null };
    metricValue: string;
    metricLabel: string;
    metricMethod: string;
  };
};

const NavigationFeaturesContext = createContext<NavigationFeatures | null>(null);

export function NavigationFeaturesProvider({
  features,
  children,
}: {
  features: NavigationFeatures;
  children: ReactNode;
}) {
  return (
    <NavigationFeaturesContext.Provider value={features}>
      {children}
    </NavigationFeaturesContext.Provider>
  );
}

export function useNavigationFeatures(): NavigationFeatures {
  const features = useContext(NavigationFeaturesContext);
  if (!features) {
    throw new Error(
      "Navigation features are missing: render the nav inside NavigationFeaturesProvider (the root layout provides it).",
    );
  }
  return features;
}
